/**
 * Happy Birthday Sing-Along Voice Type Detector
 * Minimal version - YIN pitch detection + Typical Pitch Classification only
 */

import { AudioProcessor } from './audio-processor';
import { detectPitchYIN, PitchResult } from './yin';
import { classifyByTypicalPitch, TypicalPitchClassification } from './voice-types';
import { frequencyToNoteName, medianFilter } from './utils';

// Web Speech API types (not fully supported in all browsers)
interface SpeechRecognitionResultItem {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResultItem;
  [index: number]: SpeechRecognitionResultItem;
}

interface SpeechRecognitionResultListWrapper {
  length: number;
  [index: number]: SpeechRecognitionResultList;
}

interface SpeechRecognitionEventLocal {
  results: SpeechRecognitionResultListWrapper;
}

interface SpeechRecognitionErrorEventLocal {
  error: string;
}

export type State = 'ready' | 'countdown' | 'singing' | 'results';

export interface WordPitchData {
  frequencies: number[];
  min: number | null;
  max: number | null;
  median: number | null;
}

export interface LyricWord {
  text: string;
}

export interface LyricLine {
  line: number;
  words: LyricWord[];
}

export type WordState = 'upcoming' | 'current' | 'completed' | 'skipped';

export interface SingAlongResult {
  typicalPitch: number | null;
  typicalPitchNote: string | null;
  classification: TypicalPitchClassification | null;
  minFrequency: number;
  maxFrequency: number;
  minNote: string;
  maxNote: string;
  wordPitchData: WordPitchData[];
}

export interface SingAlongCallbacks {
  onStateChange?: (state: State) => void;
  onCountdownTick?: (count: number) => void;
  onPitchDetected?: (frequency: number, confidence: number, noteName: string) => void;
  onWordAdvance?: (wordIndex: number, wordState: WordState) => void;
  onAudioLevel?: (level: number) => void;
  onResult?: (result: SingAlongResult) => void;
  onError?: (error: string) => void;
  onNameDetected?: (name: string, wordIndex: number) => void;
}

const LYRICS: LyricLine[] = [
  { line: 0, words: [{ text: 'Happy' }, { text: 'birthday' }, { text: 'to' }, { text: 'you' }] },
  { line: 1, words: [{ text: 'Happy' }, { text: 'birthday' }, { text: 'to' }, { text: 'you' }] },
  { line: 2, words: [{ text: 'Happy' }, { text: 'birthday' }, { text: 'dear' }, { text: '[name]' }] },
  { line: 3, words: [{ text: 'Happy' }, { text: 'birthday' }, { text: 'to' }, { text: 'you' }] }
];

const LOOK_AHEAD_WINDOW = 2;
// Minimum time (ms) between matching the same word type (e.g., "happy", "birthday")
const WORD_MATCH_COOLDOWN_MS = 2000;

export class HappyBirthdaySingAlong {
  private audioProcessor: AudioProcessor;
  private state: State = 'ready';
  private isRunning = false;
  private animationFrameId: number | null = null;

  private currentWordIndex = 0;
  private wordStates: WordState[] = [];
  private wordPitchData: WordPitchData[] = [];

  private detectedFrequencies: number[] = [];
  private minFrequency = Infinity;
  private maxFrequency = 0;
  private pitchBuffer: number[] = [];

  private recognition: any = null;
  private speechRecognitionSupported = false;
  private lastProcessedTranscriptForResult: Map<number, string> = new Map();
  private detectedName: string | null = null;
  // Track last match time per word type (e.g., "happy" -> timestamp)
  private lastWordMatchTime: Map<string, number> = new Map();

  private callbacks: SingAlongCallbacks;
  private autoFinishTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(callbacks: SingAlongCallbacks = {}) {
    this.callbacks = callbacks;
    this.audioProcessor = new AudioProcessor();
    this.initializeWordStates();
    this.initSpeechRecognition();
  }

  getLyrics(): LyricLine[] {
    return LYRICS;
  }

  getState(): State {
    return this.state;
  }

  getCurrentWordIndex(): number {
    return this.currentWordIndex;
  }

  getWordStates(): WordState[] {
    return [...this.wordStates];
  }

  getDetectedName(): string | null {
    return this.detectedName;
  }

  private initializeWordStates(): void {
    const totalWords = LYRICS.reduce((sum, line) => sum + line.words.length, 0);
    this.wordStates = new Array(totalWords).fill('upcoming') as WordState[];
    this.wordStates[0] = 'current';
    this.currentWordIndex = 0;

    this.wordPitchData = new Array(totalWords).fill(null).map(() => ({
      frequencies: [],
      min: null,
      max: null,
      median: null
    }));
  }

  private initSpeechRecognition(): void {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      this.speechRecognitionSupported = false;
      return;
    }

    this.speechRecognitionSupported = true;
    this.recognition = new SpeechRecognition();

    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';
    this.recognition.maxAlternatives = 3;

    this.recognition.onresult = (event: SpeechRecognitionEventLocal) => {
      this.handleSpeechResult(event);
    };

    this.recognition.onerror = (event: SpeechRecognitionErrorEventLocal) => {
      if (event.error === 'not-allowed') {
        this.callbacks.onError?.('Microphone access denied for speech recognition.');
      }
    };

    this.recognition.onend = () => {
      if (this.isRunning && this.state === 'singing') {
        try {
          this.recognition?.start();
        } catch (_e) {
          // Already running
        }
      }
    };
  }

  private handleSpeechResult(event: SpeechRecognitionEventLocal): void {
    if (!this.isRunning || this.state !== 'singing') return;

    const lastResultIndex = event.results.length - 1;
    const result = event.results[lastResultIndex];
    if (!result) return;

    // Get what we already processed for this result index
    const previousTranscript = this.lastProcessedTranscriptForResult.get(lastResultIndex) ?? '';

    for (let i = 0; i < result.length; i++) {
      const item = result[i];
      if (!item) continue;
      const transcript = item.transcript.toLowerCase().trim();

      // Find the NEW part of the transcript (what was just added)
      let newPart = transcript;
      if (previousTranscript && transcript.startsWith(previousTranscript)) {
        newPart = transcript.slice(previousTranscript.length).trim();
      } else if (previousTranscript && transcript.includes(previousTranscript)) {
        // Handle cases where previous is somewhere in the middle
        newPart = transcript.replace(previousTranscript, '').trim();
      }

      // Skip if no new content
      if (!newPart) continue;

      const matchedWordIndex = this.matchRecognizedWord(newPart);

      if (matchedWordIndex !== -1) {
        console.log(`[Speech] Result ${lastResultIndex}: new="${newPart}" (full="${transcript}") → word ${matchedWordIndex}`);
        this.lastProcessedTranscriptForResult.set(lastResultIndex, transcript);
        this.advanceToWord(matchedWordIndex);
        break;
      }
    }
  }

  private getAllWords(): string[] {
    const allWords: string[] = [];
    for (const lyricLine of LYRICS) {
      for (const word of lyricLine.words) {
        allWords.push(word.text);
      }
    }
    return allWords;
  }

  private matchRecognizedWord(transcript: string): number {
    const allWords = this.getAllWords();
    const windowEnd = Math.min(this.currentWordIndex + LOOK_AHEAD_WINDOW, allWords.length);
    const now = Date.now();

    // Words that are part of the song lyrics (to exclude from name detection)
    const songWords = new Set(['happy', 'birthday', 'to', 'you', 'dear']);

    for (let i = this.currentWordIndex; i < windowEnd; i++) {
      const word = allWords[i];
      if (!word) continue;
      const expectedWord = word.toLowerCase();

      if (expectedWord === '[name]') {
        // Extract name from transcript - accept any word that's not a song lyric
        const transcriptWords = transcript.split(/\s+/).filter(w => w.length > 0);

        // Find a word that's not part of the song lyrics
        for (const spokenWord of transcriptWords) {
          const cleanWord = spokenWord.replace(/[^a-zA-Z]/g, '').toLowerCase();
          if (cleanWord.length > 0 && !songWords.has(cleanWord)) {
            // Found a name - capitalize first letter
            const detectedName = cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1);
            this.detectedName = detectedName;
            this.callbacks.onNameDetected?.(detectedName, i);
            console.log(`[Speech] Detected name: "${detectedName}" from transcript: "${transcript}"`);
            return i;
          }
        }

        // If this is the current word and we have some transcript, accept it
        // (user might be singing something we didn't catch as a distinct word)
        if (i === this.currentWordIndex && transcript.length > 0) {
          return i;
        }
        continue;
      }

      // Check if this word matches
      const matches = transcript.includes(expectedWord) ||
        (expectedWord.length >= 4 && transcript.includes(expectedWord.substring(0, 4))) ||
        this.getWordVariations(expectedWord).some(variation => transcript.includes(variation));

      if (matches) {
        // Check cooldown - don't match the same word type too quickly
        const lastMatchTime = this.lastWordMatchTime.get(expectedWord) ?? 0;
        const timeSinceLastMatch = now - lastMatchTime;

        if (timeSinceLastMatch < WORD_MATCH_COOLDOWN_MS) {
          console.log(`[Speech] Skipping "${expectedWord}" - cooldown (${timeSinceLastMatch}ms < ${WORD_MATCH_COOLDOWN_MS}ms)`);
          continue; // Skip this word, try next in window
        }

        // Update last match time for this word type
        this.lastWordMatchTime.set(expectedWord, now);
        return i;
      }
    }

    return -1;
  }

  private getWordVariations(word: string): string[] {
    const variations = [word];
    switch (word) {
      case 'happy':
        variations.push('appy', 'happier', 'have');
        break;
      case 'birthday':
        variations.push('birth', 'birthdays', "birthday's", 'day');
        break;
      case 'dear':
        variations.push('deer', 'dare', 'here');
        break;
      case 'to':
        variations.push('two', 'too', 'do');
        break;
      case 'you':
        variations.push('u', 'yo', 'yoo');
        break;
    }
    return variations;
  }

  private calculateMedian(frequencies: number[]): number | null {
    if (!frequencies || frequencies.length === 0) return null;
    const sorted = [...frequencies].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2) {
      return sorted[mid] ?? null;
    }
    const left = sorted[mid - 1];
    const right = sorted[mid];
    if (left === undefined || right === undefined) return null;
    return (left + right) / 2;
  }

  private getTypicalPitch(): number | null {
    const medians = this.wordPitchData
      .filter(w => w.median !== null)
      .map(w => w.median as number);

    if (medians.length === 0) return null;

    const sorted = [...medians].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2) {
      return sorted[mid] ?? null;
    }
    const left = sorted[mid - 1];
    const right = sorted[mid];
    if (left === undefined || right === undefined) return null;
    return (left + right) / 2;
  }

  private advanceToWord(wordIndex: number): void {
    if (wordIndex < this.currentWordIndex) return;

    if (this.currentWordIndex >= 0 && this.currentWordIndex < this.wordPitchData.length) {
      const wordData = this.wordPitchData[this.currentWordIndex];
      if (wordData) {
        wordData.median = this.calculateMedian(wordData.frequencies);
      }
    }

    for (let i = this.currentWordIndex; i < wordIndex; i++) {
      if (this.wordStates[i] === 'current' || this.wordStates[i] === 'upcoming') {
        this.wordStates[i] = 'skipped';
        this.callbacks.onWordAdvance?.(i, 'skipped');
      }
    }

    this.wordStates[wordIndex] = 'completed';
    if (wordIndex >= 0 && wordIndex < this.wordPitchData.length) {
      const wordData = this.wordPitchData[wordIndex];
      if (wordData) {
        wordData.median = this.calculateMedian(wordData.frequencies);
      }
    }
    this.callbacks.onWordAdvance?.(wordIndex, 'completed');

    const totalWords = this.getAllWords().length;
    if (wordIndex + 1 < totalWords) {
      this.currentWordIndex = wordIndex + 1;
      this.wordStates[this.currentWordIndex] = 'current';
      this.callbacks.onWordAdvance?.(this.currentWordIndex, 'current');

      // If 1-2 words left, start a 2-second auto-finish timer
      const wordsRemaining = totalWords - this.currentWordIndex;
      if (wordsRemaining <= 2 && !this.autoFinishTimeoutId) {
        this.autoFinishTimeoutId = setTimeout(() => {
          if (this.isRunning && this.state === 'singing') {
            this.finish();
          }
        }, 2000);
      }
    } else {
      this.currentWordIndex = wordIndex;
      setTimeout(() => this.finish(), 1000);
    }
  }

  async start(): Promise<void> {
    try {
      await this.audioProcessor.initialize();
      await this.audioProcessor.resume();
      this.startCountdown();
    } catch (error) {
      this.callbacks.onError?.((error as Error).message);
    }
  }

  private startCountdown(): void {
    this.state = 'countdown';
    this.callbacks.onStateChange?.('countdown');

    let count = 3;
    this.callbacks.onCountdownTick?.(count);

    const countdownInterval = setInterval(() => {
      count--;
      if (count > 0) {
        this.callbacks.onCountdownTick?.(count);
      } else {
        clearInterval(countdownInterval);
        this.startSinging();
      }
    }, 1000);
  }

  private startSinging(): void {
    this.state = 'singing';
    this.isRunning = true;
    this.callbacks.onStateChange?.('singing');

    this.initializeWordStates();
    this.detectedFrequencies = [];
    this.minFrequency = Infinity;
    this.maxFrequency = 0;
    this.pitchBuffer = [];
    this.lastProcessedTranscriptForResult.clear();
    this.lastWordMatchTime.clear();

    if (this.speechRecognitionSupported && this.recognition) {
      try {
        this.recognition.start();
      } catch (_e) {
        // Already running
      }
    }

    this.detectPitch();
  }

  private detectPitch(): void {
    if (!this.isRunning) return;

    const buffer = this.audioProcessor.getAudioBuffer();
    const sampleRate = this.audioProcessor.getSampleRate();

    if (buffer && this.audioProcessor.isSignalPresent()) {
      const result: PitchResult = detectPitchYIN(buffer, sampleRate);

      if (result && result.frequency > 0 && result.confidence > 0.5) {
        const filteredFrequency = medianFilter(this.pitchBuffer, result.frequency, 5);

        this.detectedFrequencies.push(filteredFrequency);
        if (filteredFrequency < this.minFrequency) {
          this.minFrequency = filteredFrequency;
        }
        if (filteredFrequency > this.maxFrequency) {
          this.maxFrequency = filteredFrequency;
        }

        if (this.currentWordIndex >= 0 && this.currentWordIndex < this.wordPitchData.length) {
          const wordData = this.wordPitchData[this.currentWordIndex];
          if (wordData) {
            wordData.frequencies.push(filteredFrequency);

            if (wordData.min === null || filteredFrequency < wordData.min) {
              wordData.min = filteredFrequency;
            }
            if (wordData.max === null || filteredFrequency > wordData.max) {
              wordData.max = filteredFrequency;
            }
          }
        }

        const noteName = frequencyToNoteName(filteredFrequency);
        this.callbacks.onPitchDetected?.(filteredFrequency, result.confidence, noteName);
      }
    }

    const level = this.audioProcessor.getAudioLevel();
    this.callbacks.onAudioLevel?.(level);

    this.animationFrameId = requestAnimationFrame(() => this.detectPitch());
  }

  finish(): void {
    this.isRunning = false;
    this.state = 'results';
    this.callbacks.onStateChange?.('results');

    if (this.autoFinishTimeoutId) {
      clearTimeout(this.autoFinishTimeoutId);
      this.autoFinishTimeoutId = null;
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.recognition && this.speechRecognitionSupported) {
      try {
        this.recognition.stop();
      } catch (_e) {
        // Already stopped
      }
    }

    this.audioProcessor.stop();

    const typicalPitch = this.getTypicalPitch();
    const classification = typicalPitch ? classifyByTypicalPitch(typicalPitch) : null;

    const result: SingAlongResult = {
      typicalPitch,
      typicalPitchNote: typicalPitch ? frequencyToNoteName(typicalPitch) : null,
      classification,
      minFrequency: this.minFrequency === Infinity ? 0 : this.minFrequency,
      maxFrequency: this.maxFrequency,
      minNote: this.minFrequency !== Infinity ? frequencyToNoteName(this.minFrequency) : '-',
      maxNote: this.maxFrequency > 0 ? frequencyToNoteName(this.maxFrequency) : '-',
      wordPitchData: this.wordPitchData
    };

    this.callbacks.onResult?.(result);
  }

  restart(): void {
    if (this.recognition && this.speechRecognitionSupported) {
      try {
        this.recognition.stop();
      } catch (_e) {
        // Already stopped
      }
    }

    this.state = 'ready';
    this.isRunning = false;
    this.detectedFrequencies = [];
    this.minFrequency = Infinity;
    this.maxFrequency = 0;
    this.pitchBuffer = [];
    this.lastProcessedTranscriptForResult.clear();
    this.lastWordMatchTime.clear();
    this.detectedName = null;

    if (this.autoFinishTimeoutId) {
      clearTimeout(this.autoFinishTimeoutId);
      this.autoFinishTimeoutId = null;
    }

    this.initializeWordStates();
    this.callbacks.onStateChange?.('ready');
  }

  destroy(): void {
    this.isRunning = false;

    if (this.autoFinishTimeoutId) {
      clearTimeout(this.autoFinishTimeoutId);
      this.autoFinishTimeoutId = null;
    }

    if (this.recognition && this.speechRecognitionSupported) {
      try {
        this.recognition.stop();
      } catch (_e) {
        // Already stopped
      }
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.audioProcessor.stop();
  }
}
